import { roundMoney } from './formatMoney';

/** Fixed Israeli VAT rate for amount recalculation. */
export const ISRAEL_VAT_RATE = 0.18;

/** Rounding tolerance when checking net + VAT ≈ total (1 decimal money). */
export const AMOUNT_COHERENCE_TOLERANCE = 0.1;

export type AmountTriplet = {
  amount: number;
  vatAmount: number;
  totalAmount: number | undefined;
};

const hasValue = (n: number | undefined | null): n is number =>
  n !== undefined && n !== null && Number.isFinite(n) && Math.abs(n) > 0;

export function amountsFromNet(net: number): AmountTriplet {
  const amount = roundMoney(net);
  const vatAmount = roundMoney(amount * ISRAEL_VAT_RATE);
  const totalAmount = roundMoney(amount + vatAmount);
  return { amount, vatAmount, totalAmount };
}

export function amountsFromVat(vat: number): AmountTriplet {
  const vatAmount = roundMoney(vat);
  const amount = roundMoney(vatAmount / ISRAEL_VAT_RATE);
  const totalAmount = roundMoney(amount + vatAmount);
  return { amount, vatAmount, totalAmount };
}

export function amountsFromTotal(total: number): AmountTriplet {
  const totalAmount = roundMoney(total);
  const amount = roundMoney(totalAmount / (1 + ISRAEL_VAT_RATE));
  const vatAmount = roundMoney(totalAmount - amount);
  return { amount, vatAmount, totalAmount };
}

export function areAmountsCoherent(
  net: number | undefined | null,
  vat: number | undefined | null,
  total: number | undefined | null,
  tolerance = AMOUNT_COHERENCE_TOLERANCE
): boolean {
  if (!hasValue(net) || !hasValue(vat) || !hasValue(total)) return false;
  return Math.abs(roundMoney(net + vat) - roundMoney(total)) <= tolerance;
}

/**
 * Resolve OCR/DB amounts for the editor:
 * - If net + VAT ≈ total → keep all three
 * - If inconsistent and total exists → keep total only; clear net + VAT
 * - Otherwise keep whatever individual values are present
 */
export function resolveLoadedAmounts(
  net: number | undefined | null,
  vat: number | undefined | null,
  total: number | undefined | null
): AmountTriplet {
  const netN = hasValue(net) ? roundMoney(net) : 0;
  const vatN = hasValue(vat) ? roundMoney(vat) : 0;
  const totalN = hasValue(total) ? roundMoney(total) : undefined;

  const hasNet = hasValue(net);
  const hasVat = hasValue(vat);
  const hasTotal = hasValue(total);

  if (hasNet && hasVat && hasTotal) {
    if (areAmountsCoherent(netN, vatN, totalN)) {
      return { amount: netN, vatAmount: vatN, totalAmount: totalN };
    }
    // Inconsistent: keep total only (clearest on Israeli invoices)
    return { amount: 0, vatAmount: 0, totalAmount: totalN };
  }

  return {
    amount: hasNet ? netN : 0,
    vatAmount: hasVat ? vatN : 0,
    totalAmount: hasTotal ? totalN : undefined,
  };
}

/** No-VAT expenses: the sum is the total, net equals total, VAT is cleared. */
export function amountsForNoVat(
  net: number | undefined | null,
  vat: number | undefined | null,
  total: number | undefined | null
): AmountTriplet {
  const netN = hasValue(net) ? roundMoney(net) : 0;
  const vatN = hasValue(vat) ? roundMoney(vat) : 0;
  const totalN = hasValue(total) ? roundMoney(total) : undefined;
  const resolvedTotal =
    totalN !== undefined
      ? totalN
      : hasValue(net) && hasValue(vat)
        ? roundMoney(netN + vatN)
        : hasValue(net)
          ? netN
          : undefined;
  if (resolvedTotal === undefined) {
    return { amount: 0, vatAmount: 0, totalAmount: undefined };
  }
  return { amount: resolvedTotal, vatAmount: 0, totalAmount: resolvedTotal };
}
